import { Droplets, Home as HomeIcon, ShieldX, Waves } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import CheckList from "@/components/CheckList";
import CtaBand from "@/components/CtaBand";
import { gutterServices, images, business } from "@/data/site";

const schema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Gutter Installation and Repair",
  provider: { "@type": "RoofingContractor", name: business.name, telephone: "+1-215-806-5038" },
  areaServed: "Bucks County, Pennsylvania",
};

const protects = [
  { icon: ShieldX, title: "Prevents Water Damage", body: "Properly working gutters channel rainwater away from your home, reducing the risk of water damage." },
  { icon: HomeIcon, title: "Protects Your Foundation", body: "Good drainage keeps water from pooling around your foundation, helping prevent costly foundation issues." },
  { icon: Waves, title: "Saves Fascia & Roof Edge", body: "Clean, intact gutters protect fascia boards and roof edges from rot and water-related damage." },
];

export default function Gutters() {
  return (
    <>
      <Seo
        title="Gutters & Siding in Bucks County, PA | Future Roofing and Repair"
        description="Gutter installation & repair, siding, fascia, soffit and drainage solutions in Bucks County, PA. Protect your roofline, exterior and foundation. Call 215-806-5038 for a free estimate."
        keywords="Gutter installation Bucks County, Gutter repair Bristol PA, Siding Bucks County, Fascia and soffit, Roofline protection, Drainage solutions"
        schema={schema}
      />
      <PageHero
        kicker="Gutters & Siding"
        title="Gutters, Siding & Roofline Protection"
        subtitle="Gutter installation, repair, siding, fascia, soffit, and drainage solutions that protect your roofline, exterior, and home."
        image={images.gutters}
      />

      <section className="bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <SectionHeading kicker="Our Services" title="Gutter, Siding & Roofline Services" />
            <p className="text-charcoal/80 leading-relaxed mb-6">
              Your gutters and siding do more than shed water — they protect the entire structure and
              exterior of your home. We handle the full roofline:
            </p>
            <CheckList items={gutterServices} columns={1} />
          </div>
          <div className="bg-charcoal text-cream border border-charcoal/20 shadow-md p-8">
            <Droplets className="w-10 h-10 text-gold mb-4" />
            <h3 className="font-heading text-3xl uppercase">Why Gutters & Siding Matter</h3>
            <p className="mt-4 text-cream/85 leading-relaxed">
              When gutters fail, water has nowhere to go — leading to water damage, foundation problems,
              fascia damage, and roof-edge deterioration. Quality siding, fascia, and soffit round out
              your home's exterior protection. Keeping them in good shape is one of the most affordable
              ways to protect your home.
            </p>
            <a
              href={business.phoneHref}
              data-testid="gutters-call"
              className="mt-6 inline-flex items-center gap-2 bg-rust text-cream border border-cream/40 shadow-md hover:-translate-y-0.5 transition-all font-heading text-xl uppercase px-6 py-3"
            >
              Get a Gutter Estimate
            </a>
          </div>
        </div>
      </section>

      <section className="bg-cream-200 border-y border-charcoal/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <SectionHeading kicker="The Payoff" title="How Gutters Protect Your Home" center />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {protects.map((p) => (
              <div key={p.title} className="bg-cream border border-charcoal/20 shadow-md p-7">
                <p.icon className="w-9 h-9 text-rust mb-4" />
                <h3 className="font-heading text-xl uppercase text-charcoal">{p.title}</h3>
                <p className="mt-2 text-sm text-charcoal/75 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        kicker="Protect Your Home"
        title="Request a Free Estimate"
        text="New gutters, siding, fascia, or soffit — we'll help protect your roofline and exterior."
      />
    </>
  );
}
